import { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { useContext } from 'react';
import { flushSync } from 'react-dom';
import { Button, Popover, Select, Spin, message } from 'antd';
import { DeleteOutlined, DownOutlined } from '@ant-design/icons';
import { PromptEditModal } from '../components';
import MaterialLibraryModal from '../../components/MaterialLibraryModal';
import { uploadFlowImage } from '../../execution';
import Utils from '@utils/utils';
import eventBus from '@utils/eventBus';
import { AppContext } from '@utils/AppContext';
import { RESOLUTION_RATIO } from '@utils/CONST';
import styles from './index.module.scss';

const LARGE_ICON = 'https://photostudio-ai-oss.arcsoft.com.cn/photostudio-opshub/FreeCanvas/large.svg';
const FLOW_LINK_PLACEHOLDER_ICON = 'https://photostudio-ai-oss.arcsoft.com.cn/photostudio-opshub/FreeCanvas/node-image.svg';

const SLOT_COUNT = 14;
const MODEL_OPTIONS = [
    { value: 'Nano2', label: 'Nano2' },
    { value: 'NanoPro', label: 'NanoPro' },
];
const RATIO_OPTIONS = [
    { value: RESOLUTION_RATIO.SMART, label: '智能', previewClassName: 'ratioPreviewModel' },
    { value: RESOLUTION_RATIO.SQUARE, label: '1:1', previewClassName: 'ratioPreviewSquare' },
    { value: RESOLUTION_RATIO.THREE_FOUR, label: '3:4', previewClassName: 'ratioPreviewPortrait' },
    { value: RESOLUTION_RATIO.FOUR_THREE, label: '4:3', previewClassName: 'ratioPreviewLandscape' },
    { value: RESOLUTION_RATIO.NINE_SIXTEEN, label: '9:16', previewClassName: 'ratioPreviewTall' },
    { value: RESOLUTION_RATIO.SIXTEEN_NINE, label: '16:9', previewClassName: 'ratioPreviewWide' },
    { value: RESOLUTION_RATIO.TWO_THREE, label: '2:3', previewClassName: 'ratioPreviewTwoThree' },
    { value: RESOLUTION_RATIO.THREE_TWO, label: '3:2', previewClassName: 'ratioPreviewThreeTwo' },
];
const RESOLUTION_OPTIONS = [
    { value: '2K', label: '2K' },
    { value: '4K', label: '4K' },
];
const COUNT_OPTIONS = [
    { value: 1, label: '1张' },
    { value: 2, label: '2张' },
    { value: 4, label: '4张' },
    { value: 8, label: '8张' },
];
const DEFAULT_GENERATE_COUNT = 4;
const getMaterialItemUrl = (item) => item?.imageUrlWithVersion || item?.imageUrl || '';

const buildUploadItems = (manualUploads = [], linkedUploads = [], preferredOrder = []) => {
    const manualQueue = manualUploads
        .map((url, index) => ({ type: 'manual', url, manualIndex: index }))
        .filter((item) => !!item.url);
    const linkedQueue = linkedUploads
        .map((item, index) => ({
            type: 'linked',
            url: item?.url || '',
            linkedIndex: index,
            linkedItem: item,
        }))
        .filter((item) => !!item.url);
    const items = [];
    const consumeItem = (queue, url) => {
        const index = queue.findIndex((item) => item.url === url);
        if (index < 0) return false;
        items.push(queue.splice(index, 1)[0]);
        return true;
    };
    const consumeLinkedItem = (edgeId) => {
        const index = linkedQueue.findIndex((item) => String(item.linkedItem?.edgeId || '') === String(edgeId || ''));
        if (index < 0) return false;
        items.push(linkedQueue.splice(index, 1)[0]);
        return true;
    };
    const consumeManualItem = (url) => consumeItem(manualQueue, url);

    (Array.isArray(preferredOrder) ? preferredOrder : []).filter(Boolean).forEach((orderItem) => {
        if (items.length >= SLOT_COUNT) return;

        if (orderItem && typeof orderItem === 'object') {
            if (orderItem.type === 'linked' && consumeLinkedItem(orderItem.edgeId)) return;
            if (orderItem.type === 'manual' && consumeManualItem(orderItem.url)) return;
            if (consumeItem(manualQueue, orderItem.url)) return;
            consumeItem(linkedQueue, orderItem.url);
            return;
        }

        if (consumeItem(manualQueue, orderItem)) return;
        consumeItem(linkedQueue, orderItem);
    });

    manualQueue.concat(linkedQueue).forEach((item) => {
        if (items.length < SLOT_COUNT) {
            items.push(item);
        }
    });

    return items;
};

const extractManualUploads = (manualUploads = [], linkedUploads = []) => {
    if (Array.isArray(manualUploads) && manualUploads.length > 0) {
        return manualUploads.filter(Boolean).slice(0, SLOT_COUNT);
    }

    const linkedUrlCountMap = linkedUploads.reduce((acc, item) => {
        const url = item?.url || '';
        if (!url) return acc;
        acc.set(url, (acc.get(url) || 0) + 1);
        return acc;
    }, new Map());

    return (Array.isArray(manualUploads) ? manualUploads : [])
        .filter(Boolean)
        .filter((url) => {
            const remainCount = linkedUrlCountMap.get(url) || 0;
            if (remainCount <= 0) return true;
            linkedUrlCountMap.set(url, remainCount - 1);
            return false;
        })
        .slice(0, SLOT_COUNT);
};

const isSameValue = (left, right) => JSON.stringify(left) === JSON.stringify(right);
const isValidCount = (count) => COUNT_OPTIONS.some((option) => option.value === count);
const normalizeCount = (count) => (isValidCount(count) ? count : DEFAULT_GENERATE_COUNT);
const countNonMaterialUploads = (uploads = [], materialSelections = []) => {
    const materialUrlCountMap = (Array.isArray(materialSelections) ? materialSelections : []).reduce((acc, item) => {
        const url = getMaterialItemUrl(item);
        if (!url) return acc;
        acc.set(url, (acc.get(url) || 0) + 1);
        return acc;
    }, new Map());

    return (Array.isArray(uploads) ? uploads : []).filter(Boolean).reduce((count, url) => {
        const remainCount = materialUrlCountMap.get(url) || 0;
        if (remainCount > 0) {
            materialUrlCountMap.set(url, remainCount - 1);
            return count;
        }
        return count + 1;
    }, 0);
};

const CreativeImg2ImgPanel = ({
    nodeId,
    nodeData,
    onChange,
    disabled = false,
}) => {
    const { globalUser } = useContext(AppContext);
    const inputRef = useRef(null);
    const promptInputRef = useRef(null);
    const panelState = nodeData?.panelState || {};
    const inputParams = nodeData?.inputParams || {};
    const linkedUploads = useMemo(
        () => (Array.isArray(panelState.linkedUploads) ? panelState.linkedUploads : []),
        [panelState.linkedUploads],
    );
    const getCurrentManualUploads = useCallback(() => (
        extractManualUploads(
            Array.isArray(panelState.manualUploads)
                ? panelState.manualUploads
                : Array.isArray(panelState.uploads)
                    ? panelState.uploads
                    : Array.isArray(inputParams.imageUrls)
                        ? inputParams.imageUrls
                        : [],
            linkedUploads,
        )
    ), [inputParams.imageUrls, linkedUploads, panelState.manualUploads, panelState.uploads]);
    const [uploads, setUploads] = useState(() => (
        getCurrentManualUploads()
    ));
    const [prompt, setPrompt] = useState(panelState.prompt ?? inputParams.prompt ?? '');
    const [model, setModel] = useState(
        panelState.model
        || inputParams.model
        || MODEL_OPTIONS[1].value,
    );
    const [ratio, setRatio] = useState(
        panelState.aspectRatio
        || inputParams.aspectRatio
        || RESOLUTION_RATIO.THREE_FOUR,
    );
    const [resolution, setResolution] = useState(
        panelState.imageSize
        || inputParams.imageSize
        || '2K',
    );
    const [generateCount, setGenerateCount] = useState(
        normalizeCount(panelState.num ?? nodeData?.inputParams?.num ?? DEFAULT_GENERATE_COUNT),
    );
    const [skipSelect, setSkipSelect] = useState(panelState.skipSelect ?? !!inputParams.skipSelect);
    const [editorOpen, setEditorOpen] = useState(false);
    const [configPanelOpen, setConfigPanelOpen] = useState(false);
    const [uploadingCount, setUploadingCount] = useState(0);
    const [uploadingSlotIndex, setUploadingSlotIndex] = useState(null);
    const [materialModalOpen, setMaterialModalOpen] = useState(false);
    const [materialSelections, setMaterialSelections] = useState(() => (
        Array.isArray(panelState.materialSelections) ? panelState.materialSelections.filter(Boolean) : []
    ));
    const getCurrentUploadOrder = useCallback(() => (
        Array.isArray(panelState.uploadItems)
            ? panelState.uploadItems
            : Array.isArray(panelState.uploads)
            ? panelState.uploads.filter(Boolean)
            : Array.isArray(inputParams.imageUrls)
                ? inputParams.imageUrls.filter(Boolean)
                : []
    ), [inputParams.imageUrls, panelState.uploadItems, panelState.uploads]);
    const [uploadOrder, setUploadOrder] = useState(() => getCurrentUploadOrder());
    const uploadItems = useMemo(() => (
        buildUploadItems(uploads, linkedUploads, uploadOrder)
    ), [linkedUploads, uploadOrder, uploads]);
    const mergedUploads = useMemo(
        () => uploadItems
            .map((item) => ({
                url: item.url,
                source: item.type === 'linked' ? 'line' : 'upload',
            }))
            .filter((item) => !!item.url),
        [uploadItems],
    );
    const materialLibraryMaxSelectCount = useMemo(() => {
        const nonMaterialManualCount = countNonMaterialUploads(uploads, materialSelections);
        const linkedCount = linkedUploads.filter((item) => !!item?.url).length;
        return Math.max(1, SLOT_COUNT - nonMaterialManualCount - linkedCount);
    }, [linkedUploads, materialSelections, uploads]);
    const lastSubmittedRef = useRef('');
    const adjustPromptHeight = useCallback(() => {
        const textarea = promptInputRef.current;
        if (!textarea) return;

        const computedStyle = window.getComputedStyle(textarea);
        const lineHeight = parseFloat(computedStyle.lineHeight) || 22;
        const maxHeight = lineHeight * 6;
        const minHeight = lineHeight * 3;

        textarea.style.height = `${minHeight}px`;
        textarea.style.overflowY = 'hidden';

        const nextHeight = Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight);
        textarea.style.height = `${nextHeight}px`;
        textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
    }, []);

    useEffect(() => {
        const nextUploads = getCurrentManualUploads();
        const nextUploadOrder = getCurrentUploadOrder();
        const nextPrompt = panelState.prompt ?? inputParams.prompt ?? '';
        const nextModel = (
            panelState.model
            || inputParams.model
            || MODEL_OPTIONS[1].value
        );
        const nextRatio = (
            panelState.aspectRatio
            || inputParams.aspectRatio
            || RESOLUTION_RATIO.THREE_FOUR
        );
        const nextResolution = (
            panelState.imageSize
            || inputParams.imageSize
            || '2K'
        );
        const nextGenerateCount = normalizeCount(panelState.num ?? nodeData?.inputParams?.num ?? DEFAULT_GENERATE_COUNT);
        const nextSkipSelect = panelState.skipSelect ?? !!inputParams.skipSelect;
        const nextMaterialSelections = Array.isArray(panelState.materialSelections) ? panelState.materialSelections.filter(Boolean) : [];

        setUploads((prev) => (isSameValue(prev, nextUploads) ? prev : nextUploads));
        setUploadOrder((prev) => (isSameValue(prev, nextUploadOrder) ? prev : nextUploadOrder));
        setPrompt((prev) => (prev === nextPrompt ? prev : nextPrompt));
        setModel((prev) => (prev === nextModel ? prev : nextModel));
        setRatio((prev) => (prev === nextRatio ? prev : nextRatio));
        setResolution((prev) => (prev === nextResolution ? prev : nextResolution));
        setGenerateCount((prev) => (prev === nextGenerateCount ? prev : nextGenerateCount));
        setSkipSelect((prev) => (prev === nextSkipSelect ? prev : nextSkipSelect));
        setMaterialSelections((prev) => (isSameValue(prev, nextMaterialSelections) ? prev : nextMaterialSelections));
    }, [
        inputParams.aspectRatio,
        inputParams.imageSize,
        inputParams.imageUrls,
        inputParams.model,
        inputParams.prompt,
        inputParams.skipSelect,
        nodeData?.inputParams?.num,
        panelState.aspectRatio,
        panelState.imageSize,
        panelState.manualUploads,
        panelState.materialSelections,
        panelState.model,
        panelState.num,
        panelState.prompt,
        panelState.skipSelect,
        panelState.uploadItems,
        panelState.uploads,
        getCurrentUploadOrder,
        getCurrentManualUploads,
    ]);

    useEffect(() => {
        const currentUrls = new Set(uploads.filter(Boolean));
        setMaterialSelections((prev) => prev.filter((item) => currentUrls.has(getMaterialItemUrl(item))));
    }, [uploads]);

    useEffect(() => {
        adjustPromptHeight();
    }, [adjustPromptHeight, prompt]);

    const handleSelectMaterial = useCallback((imageUrlOrUrls, selectedItems = []) => {
        if (!imageUrlOrUrls || disabled) return;
        const nextImageUrls = Array.isArray(imageUrlOrUrls) ? imageUrlOrUrls.filter(Boolean) : [imageUrlOrUrls].filter(Boolean);
        if (nextImageUrls.length === 0) return;
        const nextSelectedItems = Array.isArray(selectedItems) ? selectedItems.filter(Boolean) : [];
        const existingMaterialUrlSet = new Set(materialSelections.map((item) => getMaterialItemUrl(item)).filter(Boolean));
        const appendUrls = nextImageUrls.filter((url) => !existingMaterialUrlSet.has(url));
        setMaterialSelections(nextSelectedItems);
        if (appendUrls.length === 0) return;
        setUploads((prev) => prev.concat(appendUrls).slice(0, SLOT_COUNT));
        setUploadOrder((prev) => (
            prev
                .concat(appendUrls.map((url) => ({ type: 'manual', url })))
                .slice(0, SLOT_COUNT)
        ));
    }, [disabled, materialSelections]);

    const handlePickImage = useCallback((event) => {
        if (disabled || uploadingCount > 0) return;
        event?.preventDefault?.();
        event?.stopPropagation?.();

        const inputElement = inputRef.current;
        if (!inputElement) return;

        if (typeof inputElement.showPicker === 'function') {
            try {
                inputElement.showPicker();
                return;
            } catch (error) {
                // Fall back to click() when showPicker is unavailable or blocked.
            }
        }

        inputElement.click();
    }, [disabled, uploadingCount]);

    const handleFileChange = useCallback(async (event) => {
        if (disabled) {
            event.target.value = '';
            return;
        }
        const fileList = Array.from(event.target.files || []);
        event.target.value = '';
        if (fileList.length === 0) return;

        const remainCount = Math.max(0, SLOT_COUNT - mergedUploads.length);
        if (remainCount === 0) {
            message.warning(`最多上传${SLOT_COUNT}张图`);
            return;
        }

        if (fileList.length > remainCount) {
            message.warning(`最多上传${SLOT_COUNT}张图`);
        }

        const nextFiles = fileList.slice(0, remainCount);
        // flushSync 强制同步渲染 loading，确保上传开始前用户立刻看到转圈
        flushSync(() => {
            setUploadingCount((prev) => prev + nextFiles.length);
            setUploadingSlotIndex(mergedUploads.length);
        });
        const uploadedUrls = await Promise.all(nextFiles.map(async (file) => {
            try {
                return await uploadFlowImage(file);
            } catch (error) {
                return '';
            }
        }));
        setUploadingCount((prev) => Math.max(0, prev - nextFiles.length));
        setUploadingSlotIndex(null);
        const validUploadedUrls = uploadedUrls.filter(Boolean);
        if (validUploadedUrls.length === 0) return;

        setUploads((prev) => prev.concat(validUploadedUrls).slice(0, SLOT_COUNT));
        // 直接将新上传的图以对象格式追加到 uploadOrder 末尾，保持自然顺序
        setUploadOrder((prev) => (
            prev
                .concat(validUploadedUrls.map((url) => ({ type: 'manual', url })))
                .slice(0, SLOT_COUNT)
        ));
    }, [disabled, mergedUploads.length, uploads]);

    const handleUploadSlotDragOver = useCallback((event) => {
        const hasFile = Array.from(event.dataTransfer?.types || []).includes('Files');
        if (!hasFile) return;

        event.preventDefault();
        event.stopPropagation();
        event.dataTransfer.dropEffect = 'copy';
    }, []);

    const handleUploadSlotDrop = useCallback(async (event, targetIndex = mergedUploads.length) => {
        if (disabled) return;
        if (uploadingCount > 0) {
            event.preventDefault();
            event.stopPropagation();
            return;
        }

        const fileList = Array.from(event.dataTransfer?.files || []);
        const imageFiles = fileList.filter((file) => file?.type?.startsWith('image/'));
        if (imageFiles.length === 0) return;

        event.preventDefault();
        event.stopPropagation();

        const normalizedTargetIndex = Math.min(
            Math.max(Number.isFinite(targetIndex) ? targetIndex : mergedUploads.length, 0),
            mergedUploads.length,
        );
        const targetItem = uploadItems[normalizedTargetIndex];
        const isReplacingExistingSlot = normalizedTargetIndex < mergedUploads.length;
        const availableCount = Math.max(0, SLOT_COUNT - mergedUploads.length + (isReplacingExistingSlot ? 1 : 0));
        if (availableCount === 0) {
            message.warning(`最多上传${SLOT_COUNT}张图`);
            return;
        }

        if (imageFiles.length > availableCount) {
            message.warning(`最多上传${SLOT_COUNT}张图`);
        }

        const nextFiles = imageFiles.slice(0, availableCount);
        setUploadingCount((prev) => prev + nextFiles.length);
        setUploadingSlotIndex(normalizedTargetIndex);
        // yield 一帧，让 React 先把 loading 状态刷新到 DOM，再开始上传
        await new Promise((resolve) => { setTimeout(resolve, 0); });
        const uploadedUrls = await Promise.all(nextFiles.map(async (file) => {
            try {
                return await uploadFlowImage(file);
            } catch (error) {
                return '';
            }
        }));
        setUploadingCount((prev) => Math.max(0, prev - nextFiles.length));
        setUploadingSlotIndex(null);
        const validUploadedUrls = uploadedUrls.filter(Boolean);
        if (validUploadedUrls.length === 0) return;

        if (targetItem?.type === 'linked' && targetItem.linkedItem?.edgeId) {
            nodeData?.onReplaceLinkedUpload?.(
                nodeId,
                targetItem.linkedItem,
                validUploadedUrls,
                normalizedTargetIndex,
            );
            return;
        }

        setUploads((prev) => {
            const nextUploads = prev.filter(Boolean);
            const nextPrimaryUrl = validUploadedUrls[0];
            const extraUrls = validUploadedUrls.slice(1);

            if (targetItem?.type === 'manual') {
                nextUploads.splice(targetItem.manualIndex, 1, nextPrimaryUrl, ...extraUrls);
                return nextUploads.slice(0, SLOT_COUNT);
            }

            if (normalizedTargetIndex < mergedUploads.length) {
                return nextUploads.concat(validUploadedUrls).slice(0, SLOT_COUNT);
            }

            return nextUploads.concat(validUploadedUrls).slice(0, SLOT_COUNT);
        });
        setUploadOrder((prev) => {
            const nextOrder = prev.slice();
            const newItems = validUploadedUrls.map((url) => ({ type: 'manual', url }));

            if (normalizedTargetIndex < nextOrder.length) {
                nextOrder.splice(normalizedTargetIndex, 1, ...newItems);
                return nextOrder.slice(0, SLOT_COUNT);
            }

            return nextOrder.concat(newItems).slice(0, SLOT_COUNT);
        });
    }, [disabled, mergedUploads.length, nodeData, nodeId, uploadItems, uploadingCount]);

    const handleRemoveImage = useCallback((index, event) => {
        if (disabled) return;
        event.stopPropagation();
        const item = uploadItems[index];
        if (item?.type !== 'manual') return;
        setUploads((prev) => prev.filter((_, itemIndex) => itemIndex !== item.manualIndex));
        setUploadOrder((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
    }, [disabled, uploadItems]);

    const handleRemoveLinkedImage = useCallback((index, event) => {
        if (disabled) return;
        event.stopPropagation();
        const linkedItem = uploadItems[index]?.linkedItem;
        if (!linkedItem?.edgeId) return;
        nodeData?.onRemoveLinkedUpload?.(nodeId, linkedItem);
    }, [disabled, nodeData, nodeId, uploadItems]);

    const handleGenerate = useCallback(({ count }) => {
        if (disabled) return;
        if (!prompt.trim() && mergedUploads.length === 0) {
            message.warning('请先输入提示词或上传参考图');
            return;
        }
        if (Number(globalUser?.membership?.level) === 0) {
            message.warning('该功能为会员用户专属,请充值会员后使用');
            eventBus.emit('recharge');
            return;
        }

        nodeData?.onExecute?.(nodeId, { count });
        nodeData?.onGenerateComplete?.(nodeId);
    }, [disabled, globalUser?.membership?.level, mergedUploads.length, nodeData, nodeId, prompt]);

    useEffect(() => {
        if (!nodeId || typeof onChange !== 'function') return;
        if (nodeData?.functionKey !== 'creativeImg2Img') return;

        const nextPatch = {
            inputParams: {
                imageUrls: mergedUploads,
                prompt,
                aspectRatio: ratio,
                imageSize: resolution,
                num: normalizeCount(generateCount),
            },
            panelState: {
                manualUploads: uploads.filter(Boolean),
                linkedUploads,
                uploadItems: uploadItems.map((item) => (
                    item.type === 'linked'
                        ? {
                            type: 'linked',
                            edgeId: item.linkedItem?.edgeId || '',
                            url: item.url,
                        }
                        : {
                            type: 'manual',
                            url: item.url,
                        }
                )),
                uploads: mergedUploads,
                prompt,
                model,
                aspectRatio: ratio,
                imageSize: resolution,
                num: normalizeCount(generateCount),
                skipSelect,
                materialSelections,
            },
        };
        const nextSignature = JSON.stringify(nextPatch);

        if (
            lastSubmittedRef.current === nextSignature
            || (
                isSameValue(nodeData?.inputParams || {}, nextPatch.inputParams)
                && isSameValue(nodeData?.panelState || {}, nextPatch.panelState)
            )
        ) {
            lastSubmittedRef.current = nextSignature;
            return;
        }

        lastSubmittedRef.current = nextSignature;
        onChange(nodeId, nextPatch);
    }, [generateCount, linkedUploads, materialSelections, mergedUploads, model, nodeData?.inputParams, nodeData?.panelState, nodeId, onChange, prompt, ratio, resolution, skipSelect, uploadItems, uploads]);

    const selectedResolutionLabel = RESOLUTION_OPTIONS.find((item) => item.value === resolution)?.label || RESOLUTION_OPTIONS[0].label;
    const selectedRatioOption = RATIO_OPTIONS.find((item) => item.value === ratio) || RATIO_OPTIONS[0];
    const selectedRatioLabel = selectedRatioOption?.label || RATIO_OPTIONS[0].label;
    const configTriggerLabel = `${selectedRatioLabel} • ${selectedResolutionLabel}`;
    const currentExecutionStatus = String(
        nodeData?.latestExecution?.status
        || nodeData?.executionStatus
        || 'idle',
    ).toLowerCase();
    const isGenerating = currentExecutionStatus === 'processing';
    const canGenerate = !disabled
        && !isGenerating
        && (prompt.trim().length > 0 || mergedUploads.length > 0);
    const totalCost = (resolution === '4K' ? 120 : 80) * normalizeCount(generateCount);
    const configPanelContent = (
        <div
            className={styles.configPanel}
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
        >
            <div className={styles.configSection}>
                <p className={styles.configTitle}>分辨率</p>
                <div className={styles.resolutionOptionGrid}>
                    {RESOLUTION_OPTIONS.map((item) => (
                        <button
                            key={item.value}
                            type="button"
                            className={[
                                styles.optionButton,
                                styles.resolutionOptionButton,
                                resolution === item.value ? styles.optionButtonActive : '',
                            ].filter(Boolean).join(' ')}
                            onMouseDown={(event) => event.stopPropagation()}
                            onClick={(event) => {
                                event.stopPropagation();
                                setResolution(item.value);
                            }}
                        >
                            {item.label}
                        </button>
                    ))}
                </div>
            </div>
            <div className={styles.configSection}>
                <p className={styles.configTitle}>比例</p>
                <div className={styles.ratioOptionGrid}>
                    {RATIO_OPTIONS.map((item) => (
                        <button
                            key={item.value}
                            type="button"
                            className={[
                                styles.optionButton,
                                styles.ratioOptionButton,
                                ratio === item.value ? styles.optionButtonActive : '',
                            ].filter(Boolean).join(' ')}
                            onMouseDown={(event) => event.stopPropagation()}
                            onClick={(event) => {
                                event.stopPropagation();
                                setRatio(item.value);
                            }}
                        >
                            <span className={[styles.ratioPreview, styles[item.previewClassName]].join(' ')}></span>
                            <span>{item.label}</span>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
    const generateButton = (
        <Button
            type="primary"
            className={styles.generateBtn}
            disabled={!canGenerate}
            onClick={canGenerate ? () => handleGenerate({ count: normalizeCount(generateCount) }) : undefined}
        >
            <span className={styles.generateBtnContent}>
                <span>{isGenerating ? '生成中' : '生成'}</span>
                {!isGenerating ? (
                    <span className={styles.generateBtnCost}>
                        <img src={require('@assets/images/arcbean3.png')} alt="" />
                        <span>{totalCost}</span>
                    </span>
                ) : null}
                {!isGenerating ? <span className={styles.teamIcon}></span> : null}
            </span>
        </Button>
    );

    return (
        <div className={`${styles.panel} ${disabled ? styles.panelDisabled : ''}`}>
            <div className={styles.content}>
                <div className={styles.uploadGrid}>
                    {uploadItems.map((item, index) => {
                        const image = item.url;
                        const linkedItem = item.type === 'linked' ? item.linkedItem : null;
                        const isLinkedPlaceholder = image === FLOW_LINK_PLACEHOLDER_ICON;
                        const displayImage = isLinkedPlaceholder ? image : Utils.getImgThumbnail(image);

                        return (
                            <button
                                key={`creative-img-slot-${index}-${linkedItem?.edgeId || 'manual'}`}
                                type="button"
                                className={`${styles.uploadSlot} ${styles.uploadSlotFilled}`}
                                onDragOver={handleUploadSlotDragOver}
                                onDrop={(event) => handleUploadSlotDrop(event, index)}
                                onClick={(event) => {
                                    if (disabled) {
                                        event.stopPropagation();
                                        return;
                                    }
                                    if (item.type === 'manual') {
                                        handleRemoveImage(index, event);
                                        return;
                                    }
                                    handleRemoveLinkedImage(index, event);
                                }}
                                aria-label={`删除输入图 ${index + 1}`}
                            >
                                {isLinkedPlaceholder ? (
                                    <span className={styles.linkedPlaceholder}>
                                        <img
                                            src={FLOW_LINK_PLACEHOLDER_ICON}
                                            alt=""
                                            className={styles.linkedPlaceholderIcon}
                                            draggable={false}
                                        />
                                        <span className={styles.linkedPlaceholderLabel}>
                                            {linkedItem?.imageSequenceLabel || `图${index + 1}`}
                                        </span>
                                    </span>
                                ) : (
                                    <img
                                        src={displayImage}
                                        alt={`输入图 ${index + 1}`}
                                        className={styles.uploadPreview}
                                        draggable={false}
                                    />
                                )}
                                <span className={styles.removeMask}>
                                    <span className={styles.removeButton}>
                                        <DeleteOutlined />
                                    </span>
                                </span>
                                {uploadingSlotIndex === index ? (
                                    <span className={styles.uploadLoading} aria-label={`输入图 ${index + 1} 上传中`}>
                                        <Spin size="small" />
                                    </span>
                                ) : null}
                            </button>
                        );
                    })}
                    {mergedUploads.length < SLOT_COUNT ? (
                        <button
                            type="button"
                            className={`${styles.uploadSlot} ${styles.uploadSlotEmpty}`}
                            onClick={uploadingCount > 0 ? undefined : handlePickImage}
                            disabled={disabled}
                            onMouseDown={(event) => event.stopPropagation()}
                            onDragOver={handleUploadSlotDragOver}
                            onDrop={(event) => handleUploadSlotDrop(event, mergedUploads.length)}
                            aria-label="添加输入图"
                        >
                            <span className={styles.plusIcon} />
                            {!uploadingCount && (
                                <span className={styles.uploadSlotActions}>
                                    <Button
                                        size="small"
                                        className={styles.uploadSlotActionBtn}
                                        onClick={(e) => { e.stopPropagation(); handlePickImage(e); }}
                                    >
                                        本地上传
                                    </Button>
                                    <Button
                                        size="small"
                                        className={styles.uploadSlotActionBtn}
                                        onClick={(e) => { e.stopPropagation(); setMaterialModalOpen(true); }}
                                    >
                                        素材库
                                    </Button>
                                </span>
                            )}
                            {uploadingSlotIndex === mergedUploads.length ? (
                                <span className={styles.uploadLoading} aria-label="输入图上传中">
                                    <Spin size="small" />
                                </span>
                            ) : null}
                        </button>
                    ) : null}
                    <input
                        ref={inputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        className={styles.fileInput}
                        onChange={handleFileChange}
                    />
                </div>

                <div className={styles.promptBox}>
                    <textarea
                        ref={promptInputRef}
                        value={prompt}
                        onChange={(event) => setPrompt(event.target.value)}
                        disabled={disabled}
                        className={styles.promptInput}
                        placeholder="请描述你想要生成的图片效果，例如：生成一张高级时尚大片，重点展现服装的版型、面料质感与穿搭效果，模特气质简约高级，不抢服装风头，画面干净精致。"
                        maxLength={1000}
                    />
                    <img
                        src={LARGE_ICON}
                        alt=""
                        width={16}
                        height={16}
                        className={styles.promptIcon}
                        draggable={false}
                        onClick={() => {
                            if (disabled) return;
                            setEditorOpen(true);
                        }}
                    />
                </div>
                <div className={styles.uploadParams}>
                    <div className={styles.leftGroups}>
                        <span className={styles.fixedModelTag}>全能模型</span>
                    </div>
                    <div className={styles.rightGroups}>
                        <Popover
                            trigger="click"
                            placement="topRight"
                            overlayClassName={styles.configPopover}
                            content={configPanelContent}
                            open={configPanelOpen}
                            onOpenChange={setConfigPanelOpen}
                        >
                            <button
                                type="button"
                                className={`${styles.configTrigger} ${configPanelOpen ? styles.configTriggerActive : ''}`}
                                onMouseDown={(event) => event.stopPropagation()}
                            >
                                <span className={styles.configTriggerText}>{configTriggerLabel}</span>
                                <DownOutlined className={`${styles.configTriggerArrow} ${configPanelOpen ? styles.configTriggerArrowOpen : ''}`} />
                            </button>
                        </Popover>
                        <Select
                            value={normalizeCount(generateCount)}
                            className={styles.countSelect}
                            onChange={(value) => setGenerateCount(normalizeCount(value))}
                            options={COUNT_OPTIONS}
                            disabled={disabled}
                            popupMatchSelectWidth={82}
                            getPopupContainer={(triggerNode) => triggerNode?.parentElement || triggerNode}
                        />
                    </div>
                    {generateButton}
                </div>
            </div>
            <PromptEditModal
                open={editorOpen}
                value={prompt}
                maxLength={1000}
                onCancel={() => setEditorOpen(false)}
                onSave={(value) => {
                    if (disabled) return;
                    setPrompt(value);
                    setEditorOpen(false);
                }}
            />
            <MaterialLibraryModal
                open={materialModalOpen}
                onClose={() => setMaterialModalOpen(false)}
                onSelect={handleSelectMaterial}
                multiple
                maxSelectCount={materialLibraryMaxSelectCount}
                initialSelectedItems={materialSelections}
                showApplyAction={false}
            />
        </div>
    );
};

export default CreativeImg2ImgPanel;
